import { useLayoutEffect, useRef } from "react";
import { useClientTranslations } from "../../lib/i18n/client";
import type { JourneyStep } from "../../types/journey";
import styles from "./StepPills.module.css";

interface StepPillsProps {
	steps: JourneyStep[];
	selectedStepId: string;
	onSelectStep: (stepId: string) => void;
}

type PillState = "default" | "completed" | "current";

function getPillState(step: JourneyStep): PillState {
	if (step.status === "completed") return "completed";
	if (step.status === "current") return "current";
	return "default";
}

export function StepPills({
	steps,
	selectedStepId,
	onSelectStep,
}: StepPillsProps) {
	const { t } = useClientTranslations("journey");
	const containerRef = useRef<HTMLDivElement>(null);
	const pillRefs = useRef<Record<string, HTMLButtonElement | null>>({});

	// Auto-scroll to the selected step on every mount + selection change.
	// Multi-pass to defeat layout race during route transitions:
	//   - useLayoutEffect runs sync after DOM mutations, before paint
	//   - Then RAF (post-paint #1)
	//   - Then RAF inside RAF (post-paint #2, after any reflow)
	// Position bias: target's left edge sits ~100px from container's left edge,
	// leaving a small peek of the previous step.
	useLayoutEffect(() => {
		if (!selectedStepId) return;
		const target = pillRefs.current[selectedStepId];
		if (!target?.parentElement) return;
		const parent = target.parentElement;
		const LEFT_PEEK_PX = 60;

		const apply = () => {
			const t2 = pillRefs.current[selectedStepId];
			if (!t2) return;
			const desired = Math.max(0, t2.offsetLeft - LEFT_PEEK_PX);
			parent.scrollLeft = desired;
		};

		apply();
		let raf2 = 0;
		const raf1 = requestAnimationFrame(() => {
			apply();
			raf2 = requestAnimationFrame(apply);
		});
		return () => {
			cancelAnimationFrame(raf1);
			if (raf2) cancelAnimationFrame(raf2);
		};
	}, [selectedStepId]);

	return (
		<section className={styles.section}>
			<div className={styles.heading}>
				<h2 className={styles.headingTitle}>{t("steps.heading")}</h2>
				<p className={styles.headingIntro}>{t("steps.intro")}</p>
			</div>
			<div
				ref={containerRef}
				className={styles.pillRow}
				role="tablist"
				aria-label={t("steps.heading")}
			>
				{steps.map((step) => {
					const state = getPillState(step);
					const isSelected = step.id === selectedStepId;
					const label = t(`stepLabels.${step.id}`, {
						defaultValue: step.title,
					});
					return (
						<button
							key={step.id}
							ref={(el) => {
								pillRefs.current[step.id] = el;
							}}
							type="button"
							role="tab"
							aria-selected={isSelected}
							aria-label={label}
							data-state={state}
							data-selected={isSelected}
							className={styles.pill}
							onClick={() => onSelectStep(step.id)}
						>
							{label}
						</button>
					);
				})}
			</div>
		</section>
	);
}
